import { createApi } from '@reduxjs/toolkit/query/react'
import { v4 as uuidv4 } from 'uuid'
import { configManager } from '../../config/configManager'
import {
  CommentResponse,
  CreateCommentResponse,
  CreateCommentWithAttachmentResponse,
  CreateTicketResponse,
  IGetTicketResponse,
  JiraAttachmentListResponse,
} from '../pages/support/SupportViewTickets/support-view-tickets.interface'
import { RootState } from '../redux/store'
import { createBaseQueryWithErrorHandling } from './baseQueryWithErrorHandling'

export const supportTicketApi = createApi({
  reducerPath: 'supportTicketApi',
  baseQuery: createBaseQueryWithErrorHandling(`${configManager.api.GATEWAY_URL}/jira`, (headers, { getState }) => {
    headers.set('x-request-id', uuidv4())
    const { token, companyToken } = (getState() as RootState).authReducer
    if (token) {
      headers.set('authorization', `Bearer ${token}`)
    }
    if (companyToken) {
      headers.set('x-company-token', companyToken)
    }
    return headers
  }),
  endpoints: (builder) => ({
    createTicket: builder.mutation<CreateTicketResponse, FormData>({
      query: (formData) => ({
        url: '/ticket',
        method: 'POST',
        body: formData,
      }),
    }),
    getTickets: builder.query<IGetTicketResponse, void>({
      query: () => ({
        url: '/tickets',
        method: 'GET',
      }),
    }),
    getComment: builder.mutation<CommentResponse, { issueKey: string }>({
      query: (body) => ({
        url: '/tickets/comments',
        method: 'POST',
        body: { ...body },
      }),
    }),
    createComment: builder.mutation<CreateCommentResponse, { issueId: string; comment: string }>({
      query: (body) => ({
        url: '/tickets/comments/create',
        method: 'POST',
        body: { ...body },
      }),
    }),
    createCommentWithAttachment: builder.mutation<CreateCommentWithAttachmentResponse, FormData>({
      query: (formData) => ({
        url: '/ticket/comments/attachments',
        method: 'POST',
        body: formData,
      }),
    }),
    getCommentAttachments: builder.query<JiraAttachmentListResponse, { issueIdOrKey: string; limit?: number; start?: number }>({
      query: ({ issueIdOrKey, limit = 50, start = 0 }) => ({
        url: `/ticket/view/attachments?issueIdOrKey=${issueIdOrKey}&limit=${limit}&start=${start}`,
        method: 'GET',
      }),
    }),
    downloadAttachment: builder.query<Blob, { issueIdOrKey: string; contentId: string }>({
      query: ({ issueIdOrKey, contentId }) => ({
        url: `/ticket/download/attachments?issueIdOrKey=${issueIdOrKey}&contentId=${contentId}`,
        method: 'GET',
        responseHandler: async (response: Response) => response.blob(),
      }),
    }),
  }),
})

export const {
  useCreateTicketMutation,
  useLazyGetTicketsQuery,
  useGetCommentMutation,
  useCreateCommentMutation,
  useCreateCommentWithAttachmentMutation,
  useLazyGetCommentAttachmentsQuery,
  useLazyDownloadAttachmentQuery,
} = supportTicketApi
